import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import api from '../utils/api';
import toast from 'react-hot-toast';

const COLORS = ['#E74C3C','#E67E22','#27AE60','#2980B9','#8E44AD',
                 '#C0392B','#1ABC9C','#F39C12','#9B59B6','#16A085',
                 '#2ECC71','#34495E','#3498DB','#E91E63','#95A5A6'];

function KpiCard({ label, value, icon, color, sub }) {
  return (
    <div style={{
      background:'#fff', borderRadius:12, padding:'20px 24px',
      boxShadow:'0 2px 8px rgba(0,0,0,0.07)', borderLeft:`4px solid ${color}`
    }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <div style={{ fontSize:12, color:'#888', fontWeight:600, textTransform:'uppercase', letterSpacing:0.5 }}>{label}</div>
          <div style={{ fontSize:26, fontWeight:700, color, marginTop:4 }}>
            ${Number(value||0).toLocaleString('en-US', { minimumFractionDigits:2, maximumFractionDigits:2 })}
          </div>
          {sub && <div style={{ fontSize:11, color:'#aaa', marginTop:4 }}>{sub}</div>}
        </div>
        <div style={{ fontSize:28 }}>{icon}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [cycle, setCycle] = useState('');
  const [loading, setLoading] = useState(true);
  const [closingMonth, setClosingMonth] = useState(false);
  const [closeNotes, setCloseNotes] = useState('');
  const [showCloseModal, setShowCloseModal] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/dashboard${cycle ? '?cycle='+cycle : ''}`);
      if (!cycle && res.data.cycle_key) setCycle(res.data.cycle_key);
      setData(res.data);
    } catch (err) { toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  }, [cycle]);

  useEffect(() => { load(); }, [load]);

  const handleCloseMonth = async () => {
    setClosingMonth(true);
    try {
      await api.post('/dashboard/close-month', { cycle_key: cycle, notes: closeNotes });
      toast.success(`Month closed & report saved!`);
      setShowCloseModal(false);
      setCloseNotes('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to close month');
    } finally { setClosingMonth(false); }
  };

  // Build trend chart data
  const trendData = React.useMemo(() => {
    if (!data?.trend) return [];
    const map = {};
    data.trend.forEach(r => {
      if (!map[r.month_key]) map[r.month_key] = { month: r.month_key };
      map[r.month_key][r.type] = parseFloat(r.total);
    });
    return Object.values(map).sort((a,b)=>a.month.localeCompare(b.month));
  }, [data]);

  const isMonthClosed = data?.closed_months?.some(m => m.month_key === cycle);
  const monthLabel = data?.cycle_label || cycle;

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', paddingTop:80 }}>
      <div style={{ fontSize:16, color:'#888' }}>⏳ Loading dashboard…</div>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h1 style={{ margin:0, fontSize:22, color:'#1E2A38', fontWeight:700 }}>📊 Dashboard</h1>
          <p style={{ margin:'4px 0 0', color:'#888', fontSize:13 }}>Personal budget overview</p>
        </div>
        <div style={{ display:'flex', gap:12, alignItems:'center' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:12, color:'#888' }}>Cycle:</span>
            <input type="month" value={cycle} onChange={e=>setCycle(e.target.value)}
              style={{ padding:'8px 12px', borderRadius:8, border:'1.5px solid #ddd', fontSize:13 }} />
          </div>
          {!isMonthClosed ? (
            <button onClick={()=>setShowCloseModal(true)} style={{
              padding:'9px 18px', borderRadius:8, border:'none',
              background:'#1E2A38', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer'
            }}>🔒 Close Month</button>
          ) : (
            <button onClick={()=>navigate('/reports')} style={{
              padding:'9px 18px', borderRadius:8, border:'none',
              background:'#27AE60', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer'
            }}>📋 View Report</button>
          )}
        </div>
      </div>

      {isMonthClosed && (
        <div style={{ background:'#FFF3CD', border:'1px solid #F5A623', borderRadius:8,
          padding:'10px 16px', marginBottom:20, fontSize:13, color:'#856404' }}>
          ✅ <strong>{monthLabel}</strong> is closed. Transactions are locked. View the report above.
        </div>
      )}

      {/* KPI Cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:24 }}>
        <KpiCard label="Total Income"   value={data?.totals?.Income}   icon="💰" color="#27AE60" sub={monthLabel} />
        <KpiCard label="Total Expenses" value={data?.totals?.Expense}  icon="💸" color="#E74C3C" sub={monthLabel} />
        <KpiCard label="Net Balance"    value={data?.net}              icon="⚖️"  color={data?.net>=0?'#2980B9':'#E74C3C'} />
        <KpiCard label="Total Savings"  value={data?.totals?.Savings}  icon="🏦" color="#8E44AD" sub={monthLabel} />
      </div>

      {/* Charts row */}
      <div style={{ display:'grid', gridTemplateColumns:'1.5fr 1fr', gap:16, marginBottom:24 }}>
        {/* Trend chart */}
        <div style={{ background:'#fff', borderRadius:12, padding:20, boxShadow:'0 2px 8px rgba(0,0,0,0.07)' }}>
          <h3 style={{ margin:'0 0 16px', fontSize:14, color:'#1E2A38' }}>📈 6-Month Trend</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={trendData} barSize={16}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize:11 }} />
              <YAxis tick={{ fontSize:11 }} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={v=>`$${Number(v).toLocaleString()}`} />
              <Legend wrapperStyle={{ fontSize:12 }} />
              <Bar dataKey="Income"  fill="#27AE60" name="Income"   radius={[3,3,0,0]} />
              <Bar dataKey="Expense" fill="#E74C3C" name="Expenses" radius={[3,3,0,0]} />
              <Bar dataKey="Savings" fill="#8E44AD" name="Savings"  radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pie chart */}
        <div style={{ background:'#fff', borderRadius:12, padding:20, boxShadow:'0 2px 8px rgba(0,0,0,0.07)' }}>
          <h3 style={{ margin:'0 0 16px', fontSize:14, color:'#1E2A38' }}>🥧 Spending by Category</h3>
          {data?.category_breakdown?.some(c=>parseFloat(c.spent)>0) ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={data.category_breakdown.filter(c=>parseFloat(c.spent)>0)}
                  dataKey="spent" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                  label={({ name, percent }) => percent>0.04 ? `${name} ${(percent*100).toFixed(0)}%` : ''}
                  labelLine={false}
                >
                  {data.category_breakdown.filter(c=>parseFloat(c.spent)>0).map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={v=>`$${Number(v).toLocaleString()}`} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height:220, display:'flex', alignItems:'center', justifyContent:'center', color:'#aaa', fontSize:13 }}>
              No expenses this month
            </div>
          )}
        </div>
      </div>

      {/* Budget vs Actual + Top Expenses */}
      <div style={{ display:'grid', gridTemplateColumns:'1.5fr 1fr', gap:16, marginBottom:24 }}>
        {/* Budget vs Actual */}
        <div style={{ background:'#fff', borderRadius:12, padding:20, boxShadow:'0 2px 8px rgba(0,0,0,0.07)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <h3 style={{ margin:0, fontSize:14, color:'#1E2A38' }}>🎯 Budget vs Actual</h3>
            <button onClick={()=>navigate('/budgets')}
              style={{ fontSize:12, color:'#2980B9', background:'none', border:'none', cursor:'pointer' }}>
              Edit Budgets →
            </button>
          </div>
          <div style={{ maxHeight:280, overflowY:'auto' }}>
            {data?.category_breakdown?.filter(c=>parseFloat(c.budget)>0||parseFloat(c.spent)>0).map((cat,i) => {
              const pct = cat.budget>0 ? Math.min(parseFloat(cat.spent)/parseFloat(cat.budget),1) : 0;
              const over = parseFloat(cat.spent) > parseFloat(cat.budget) && cat.budget > 0;
              return (
                <div key={i} style={{ marginBottom:12 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4 }}>
                    <span>{cat.icon} {cat.name}</span>
                    <span style={{ color: over?'#E74C3C':'#444' }}>
                      ${Number(cat.spent).toLocaleString()} {cat.budget>0 && `/ $${Number(cat.budget).toLocaleString()}`}
                      {over && ' ⚠️'}
                    </span>
                  </div>
                  {cat.budget > 0 && (
                    <div style={{ height:6, background:'#f0f0f0', borderRadius:3 }}>
                      <div style={{
                        height:6, borderRadius:3,
                        width:`${pct*100}%`,
                        background: over ? '#E74C3C' : pct>0.8 ? '#F5A623' : '#27AE60',
                        transition:'width 0.3s'
                      }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Top 5 + Savings Goals */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:20, boxShadow:'0 2px 8px rgba(0,0,0,0.07)' }}>
            <h3 style={{ margin:'0 0 12px', fontSize:14, color:'#1E2A38' }}>🔝 Top Expenses</h3>
            {data?.top_expenses?.length ? data.top_expenses.map((ex,i) => (
              <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                padding:'6px 0', borderBottom:'1px solid #f5f5f5', fontSize:12 }}>
                <span style={{ color:'#555' }}>
                  <span style={{ background:'#E74C3C', color:'#fff', borderRadius:10,
                    padding:'1px 6px', fontSize:10, marginRight:6 }}>#{i+1}</span>
                  {ex.icon} {ex.description || ex.category}
                </span>
                <span style={{ fontWeight:600, color:'#E74C3C' }}>${Number(ex.amount_usd).toLocaleString()}</span>
              </div>
            )) : (
              <div style={{ color:'#aaa', fontSize:12 }}>No expenses recorded</div>
            )}
          </div>
        </div>
      </div>

      {/* Savings Goals */}
      {data?.savings_goals?.length > 0 && (
        <div style={{ background:'#fff', borderRadius:12, padding:20, boxShadow:'0 2px 8px rgba(0,0,0,0.07)', marginBottom:24 }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
            <h3 style={{ margin:0, fontSize:14, color:'#1E2A38' }}>🏦 Savings Goals</h3>
            <button onClick={()=>navigate('/goals')}
              style={{ fontSize:12, color:'#2980B9', background:'none', border:'none', cursor:'pointer' }}>
              Manage →
            </button>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:12 }}>
            {data.savings_goals.map((g,i) => {
              const pct = g.target_usd > 0 ? Math.min(parseFloat(g.saved_usd)/parseFloat(g.target_usd),1) : 0;
              return (
                <div key={i} style={{ padding:12, borderRadius:8, border:'1px solid #eee' }}>
                  <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>{g.icon} {g.name}</div>
                  <div style={{ fontSize:14, fontWeight:700, color:g.color||'#2ECC71' }}>
                    ${Number(g.saved_usd).toLocaleString()} <span style={{ fontSize:10, color:'#aaa' }}>/ ${Number(g.target_usd).toLocaleString()}</span>
                  </div>
                  <div style={{ height:5, background:'#f0f0f0', borderRadius:3, marginTop:8 }}>
                    <div style={{ height:5, width:`${pct*100}%`, background:g.color||'#2ECC71', borderRadius:3 }} />
                  </div>
                  <div style={{ fontSize:10, color:'#888', marginTop:4 }}>{(pct*100).toFixed(0)}% complete</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Close Month Modal */}
      {showCloseModal && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.5)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000
        }}>
          <div style={{ background:'#fff', borderRadius:16, padding:32, width:460, maxWidth:'90vw' }}>
            <h2 style={{ margin:'0 0 8px', fontSize:18 }}>🔒 Close {monthLabel}?</h2>
            <p style={{ margin:'0 0 20px', color:'#666', fontSize:14 }}>
              This will generate a permanent monthly report snapshot and lock all transactions for this month.
              This action <strong>cannot be undone</strong>.
            </p>
            <div style={{ background:'#F0F4F8', borderRadius:8, padding:12, marginBottom:20, fontSize:13 }}>
              <div>💰 Income: <strong>${Number(data?.totals?.Income||0).toLocaleString()}</strong></div>
              <div>💸 Expenses: <strong>${Number(data?.totals?.Expense||0).toLocaleString()}</strong></div>
              <div>🏦 Savings: <strong>${Number(data?.totals?.Savings||0).toLocaleString()}</strong></div>
              <div>⚖️ Net: <strong style={{ color:data?.net>=0?'#27AE60':'#E74C3C' }}>${Number(data?.net||0).toLocaleString()}</strong></div>
            </div>
            <textarea
              placeholder="Optional notes for this month…"
              value={closeNotes} onChange={e=>setCloseNotes(e.target.value)}
              rows={3} style={{ width:'100%', padding:10, borderRadius:8, border:'1.5px solid #ddd',
                fontSize:13, boxSizing:'border-box', resize:'vertical' }}
            />
            <div style={{ display:'flex', gap:12, marginTop:16, justifyContent:'flex-end' }}>
              <button onClick={()=>setShowCloseModal(false)}
                style={{ padding:'9px 18px', borderRadius:8, border:'1.5px solid #ddd',
                  background:'#fff', fontSize:13, cursor:'pointer' }}>
                Cancel
              </button>
              <button onClick={handleCloseMonth} disabled={closingMonth}
                style={{ padding:'9px 18px', borderRadius:8, border:'none',
                  background:'#E74C3C', color:'#fff', fontSize:13, fontWeight:600,
                  cursor: closingMonth?'not-allowed':'pointer' }}>
                {closingMonth ? 'Closing…' : '🔒 Confirm Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
